import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getBlogBySlug, formatBlogContent } from '../../services/blogService';

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const blogData = await getBlogBySlug(slug);
        
        if (!blogData) {
          setError('Blog post not found');
          return;
        }
        
        // Check if blog is published (for public access)
        if (!blogData.published) {
          setError('Blog post not found');
          return;
        }
        
        setBlog(blogData);
      } catch (err) {
        console.error('Error fetching blog:', err);
        setError('Failed to load blog post');
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchBlog();
    }
  }, [slug]);

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    
    let date;
    if (timestamp.toDate) {
      // Firestore timestamp
      date = timestamp.toDate();
    } else {
      // Regular date
      date = new Date(timestamp);
    }
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading blog post...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-6xl font-bold text-gray-600 mb-4">404</h1>
          <p className="text-xl text-gray-400 mb-8">{error}</p>
          <button 
            onClick={() => navigate('/blogs')}
            className="bg-yellow-400 hover:bg-yellow-500 text-gray-900 px-6 py-3 rounded-lg transition-colors font-medium"
          >
            View All Blogs
          </button>
        </div>
      </div>
    );
  }

  if (!blog) {
    return null;
  }

  const canonicalUrl = `https://trendingchess.com/blog/${slug}`;
  const metaDescription = blog.metaDescription || blog.excerpt || (blog.body ? blog.body.replace(/<[^>]*>/g, '').replace(/[*#_~`]/g, '').substring(0, 160).trim() + '...' : `Read "${blog.title}" on TrendingChess.com.`);
  const metaImage = blog.featuredImage || 'https://trendingchess.com/IndieChess.com.png';

  return (
    <div className="min-h-screen bg-gray-900">
      <Helmet>
        <title>{blog.title} | TrendingChess.com</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={blog.title} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={metaImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={blog.title} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={metaImage} />
      </Helmet>
      {/* Header */}
      <div className="bg-yellow-400 border-b border-yellow-500">
        <div className="max-w-4xl mx-auto px-8 py-8">
          <button 
            onClick={() => navigate('/blogs')}
            className="text-gray-800 hover:text-gray-700 mb-6 inline-flex items-center transition-colors font-medium"
          >
            ← Back to Blogs
          </button>
          
          {/* Hero Image */}
          {blog.featuredImage && (
            <div className="aspect-video overflow-hidden rounded-lg border-2 border-gray-800 shadow-lg mb-6">
              <img
                src={blog.featuredImage}
                alt={blog.title}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.parentElement.style.display = 'none';
                }}
              />
            </div>
          )}

          {/* Title and Meta */}
          <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 leading-tight">
            {blog.title}
          </h1>

          <div className="flex flex-wrap items-center gap-2 md:gap-4 text-gray-800 text-sm md:text-base">
            <span>By {blog.author || 'TrendingChess Team'}</span>
            <span>•</span>
            <span>{formatDate(blog.createdAt)}</span>
            {blog.tags && blog.tags.length > 0 && (
              <>
                <span>•</span>
                <div className="flex gap-2 flex-wrap">
                  {blog.tags.map((tag, index) => (
                    <span key={index} className="bg-gray-700 text-gray-100 px-2 py-1 rounded text-xs md:text-sm">
                      {tag}
                    </span>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <article className="max-w-4xl mx-auto px-4 pb-16 mt-8">
        <div 
          className="prose prose-lg prose-invert max-w-none"
          dangerouslySetInnerHTML={{ 
            __html: formatBlogContent(blog.body) 
          }}
        />
      </article>

      {/* More Blogs Section */}
      <div className="bg-gray-800 border-t border-gray-700 py-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h3 className="text-2xl font-bold text-gray-100 mb-4">
            Explore More Chess Content
          </h3>
          <p className="text-gray-400 mb-8">
            Discover more insights about the chess community
          </p>
          <button 
            onClick={() => navigate('/blogs')}
            className="bg-yellow-400 hover:bg-yellow-500 text-gray-900 px-8 py-3 rounded-lg transition-colors inline-flex items-center gap-2 font-medium"
          >
            View All Blogs
            <span>→</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default BlogPost;